import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Notifications archive — the full history surface the bell links to.
 *
 * Patterns:
 *   • Two filter rows: Read-state (All / Unread) on top, then Severity
 *     and Category pills below. Industry parity: Linear, GitHub
 *     Notifications, Slack Activity panel.
 *   • Paginated 25 at a time with skip/limit + Prev/Next; we deliberately
 *     skipped infinite scroll for a stable archive that's easy to deep-
 *     link to and stable across re-renders (mark-as-read can re-order
 *     things when "Unread" is active).
 *   • "Mark all as read" applies to the entire user's mailbox, not just
 *     the visible filter — same semantics as Gmail's button.
 *   • Click a row → marks-read + navigates to its link_url (same as the
 *     bell). Click the avatar dot → toggles read-state without nav.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '@/lib/i18n';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, ArrowLeft, ArrowRight, Bell, CheckCheck, CheckCircle2, Filter, Info, Wrench, } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatRelative, formatWithZone, getViewerTimezone } from '@/lib/timeDisplay';
const PAGE_SIZE = 25;
const SEVERITY_LABEL = {
    all: 'All severities',
    info: 'Info',
    success: 'Success',
    warning: 'Warning',
    critical: 'Critical',
};
const CATEGORY_LABEL = {
    all: 'All categories',
    ticket: 'Tickets',
    quote: 'Vendor quotes',
};
const SEVERITY_BADGE = {
    info: { bg: 'bg-sky-100 dark:bg-sky-950/40', text: 'text-sky-700 dark:text-sky-300' },
    success: { bg: 'bg-emerald-100 dark:bg-emerald-950/40', text: 'text-emerald-700 dark:text-emerald-300' },
    warning: { bg: 'bg-amber-100 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-300' },
    critical: { bg: 'bg-rose-100 dark:bg-rose-950/40', text: 'text-rose-700 dark:text-rose-300' },
};
function severityIcon(s) {
    switch (s) {
        case 'success': return CheckCircle2;
        case 'warning': return AlertTriangle;
        case 'critical': return AlertTriangle;
        default: return Info;
    }
}
function eventIcon(eventType) {
    if (eventType.startsWith('ticket.assigned'))
        return Wrench;
    if (eventType.startsWith('ticket.'))
        return Bell;
    if (eventType.startsWith('quote.'))
        return CheckCheck;
    return Info;
}
/** Build the GET /notifications query string from the current filters
 *  + page. Kept as a pure function so the queryKey can compose with it
 *  for React Query's cache. */
function buildQuery(args) {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE_SIZE));
    p.set('skip', String(args.skip));
    if (args.readFilter === 'unread')
        p.set('only_unread', 'true');
    if (args.severity !== 'all')
        p.set('severity', args.severity);
    if (args.category !== 'all')
        p.set('event_prefix', `${args.category}.`);
    return `/notifications?${p.toString()}`;
}
export function NotificationsArchivePage() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [readFilter, setReadFilter] = useState('all');
    const [severity, setSeverity] = useState('all');
    const [category, setCategory] = useState('all');
    const [skip, setSkip] = useState(0);
    // Resetting the page index when filters change avoids landing on
    // page 5 of a filter that only has 2 pages of results.
    const setFilter = (setter) => (v) => {
        setter(v);
        setSkip(0);
    };
    const queryUrl = useMemo(() => buildQuery({ readFilter, severity, category, skip }), [readFilter, severity, category, skip]);
    const listQ = useQuery({
        queryKey: ['notifications', 'archive', queryUrl],
        queryFn: () => api.get(queryUrl),
        staleTime: 5_000,
    });
    const markRead = useMutation({
        mutationFn: (id) => api.post(`/notifications/${id}/read`, {}),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
    });
    const markAll = useMutation({
        mutationFn: () => api.post('/notifications/read-all', {}),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
    });
    const items = listQ.data?.items ?? [];
    const total = listQ.data?.total_count ?? 0;
    const showingFrom = total === 0 ? 0 : skip + 1;
    const showingTo = Math.min(skip + PAGE_SIZE, total);
    const canPrev = skip > 0;
    const canNext = skip + PAGE_SIZE < total;
    const onItemClick = (n) => {
        if (!n.is_read)
            markRead.mutate(n.id);
        if (n.link_url) {
            try {
                const url = new URL(n.link_url, window.location.origin);
                navigate(url.pathname + url.search + url.hash);
            }
            catch {
                // Bad link_url — don't blow up, just leave them on the archive.
            }
        }
    };
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: t('Notifications'), description: t('Your full activity history. Filter by read state, severity, or category.'), actions: _jsxs(Button, { size: "sm", variant: "outline", disabled: markAll.isPending || total === 0, onClick: () => markAll.mutate(), children: [_jsx(CheckCheck, { size: 14, className: "mr-1.5" }), t('Mark all as read')] }) }), _jsx(PageBody, { children: _jsxs("div", { className: "space-y-4", children: [_jsx(Card, { children: _jsxs(CardBody, { className: "flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center", children: [_jsxs("span", { className: "inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500", children: [_jsx(Filter, { size: 12 }), t('Filters')] }), _jsx(FilterGroup, { label: t('Status'), value: readFilter, options: [
                                            { value: 'all', label: 'All' },
                                            { value: 'unread', label: 'Unread only' },
                                        ], onChange: setFilter(setReadFilter) }), _jsx(FilterGroup, { label: t('Severity'), value: severity, options: Object.keys(SEVERITY_LABEL).map((k) => ({
                                            value: k,
                                            label: SEVERITY_LABEL[k],
                                        })), onChange: setFilter(setSeverity) }), _jsx(FilterGroup, { label: t('Category'), value: category, options: Object.keys(CATEGORY_LABEL).map((k) => ({
                                            value: k,
                                            label: CATEGORY_LABEL[k],
                                        })), onChange: setFilter(setCategory) })] }) }), _jsx(Card, { children: _jsx(CardBody, { className: "p-0", children: listQ.isLoading ? (_jsx("div", { className: "px-6 py-12 text-center text-sm text-slate-500", children: "Loading\u2026" })) : items.length === 0 ? (_jsxs("div", { className: "px-6 py-12 text-center", children: [_jsx(Bell, { className: "mx-auto h-10 w-10 text-slate-300" }), _jsx("div", { className: "mt-3 text-sm text-slate-500", children: readFilter === 'unread' || severity !== 'all' || category !== 'all'
                                                ? 'No notifications match these filters.'
                                                : 'No notifications yet.' })] })) : (_jsx("ul", { className: "divide-y divide-slate-200 dark:divide-slate-800", children: items.map((n) => (_jsx(NotificationRow, { n: n, onClick: () => onItemClick(n) }, n.id))) })) }) }), total > 0 && (_jsxs("div", { className: "flex items-center justify-between text-xs text-slate-500", children: [_jsxs("span", { children: ["Showing ", _jsx("strong", { className: "text-slate-700 dark:text-slate-300", children: showingFrom }), "\u2013", _jsx("strong", { className: "text-slate-700 dark:text-slate-300", children: showingTo }), " of", ' ', _jsx("strong", { className: "text-slate-700 dark:text-slate-300", children: total })] }), _jsxs("div", { className: "flex gap-2", children: [_jsxs(Button, { size: "sm", variant: "outline", disabled: !canPrev, onClick: () => setSkip(Math.max(0, skip - PAGE_SIZE)), children: [_jsx(ArrowLeft, { size: 14, className: "mr-1" }), t('Previous')] }), _jsxs(Button, { size: "sm", variant: "outline", disabled: !canNext, onClick: () => setSkip(skip + PAGE_SIZE), children: [t('Next'), _jsx(ArrowRight, { size: 14, className: "ml-1" })] })] })] }))] }) })] }));
}
// ---------- inner components -------------------------------------------
function FilterGroup({ label, value, options, onChange, }) {
    return (_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsxs("span", { className: "text-[11px] uppercase tracking-wide text-slate-400", children: [label, ":"] }), _jsx("div", { className: "flex flex-wrap gap-1", children: options.map((o) => (_jsx("button", { type: "button", onClick: () => onChange(o.value), className: cn('rounded-full border px-2.5 py-1 text-[11px] font-medium transition', value === o.value
                        ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'), children: o.label }, o.value))) })] }));
}
function NotificationRow({ n, onClick, }) {
    const SevIcon = severityIcon(n.severity);
    const EvIcon = eventIcon(n.event_type);
    const Icon = n.severity === 'info' ? EvIcon : SevIcon;
    const sevBadge = SEVERITY_BADGE[n.severity] ?? SEVERITY_BADGE.info;
    const created = new Date(n.created_at);
    return (_jsx("li", { children: _jsxs("button", { type: "button", onClick: onClick, className: cn('flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800/50', !n.is_read && 'bg-brand-50/30 dark:bg-brand-950/15'), children: [_jsx("div", { className: cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', sevBadge.bg, sevBadge.text), children: _jsx(Icon, { className: "h-4 w-4" }) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("div", { className: "flex items-baseline gap-2", children: [_jsx("span", { className: cn('truncate text-sm', n.is_read
                                        ? 'text-slate-600 dark:text-slate-400'
                                        : 'font-semibold text-slate-900 dark:text-slate-100'), children: n.title }), !n.is_read && (_jsx("span", { className: "ml-auto inline-block h-2 w-2 shrink-0 rounded-full bg-brand-500" }))] }), n.body && (_jsx("div", { className: "mt-0.5 line-clamp-2 text-xs text-slate-500", children: n.body })), _jsxs("div", { className: "mt-1 flex items-center gap-2 text-[10px] text-slate-400", children: [_jsx("span", { className: "uppercase tracking-wide", children: formatDistanceToNow(created, { addSuffix: true }) }), _jsx("span", { children: "\u00B7" }), _jsx("span", { title: formatRelative(n.created_at, getViewerTimezone()).tooltip, children: formatWithZone(n.created_at, getViewerTimezone()) }), _jsx("span", { children: "\u00B7" }), _jsx("span", { className: "font-mono", children: n.event_type })] })] })] }) }));
}
